import React from 'react'
import { cn } from '../../lib/utils'
import { IconSolid } from '../ui/Icon'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Download } from 'lucide-react'

export interface SlideNavigationProps {
  onPrev: () => void
  onNext: () => void
  onFullscreen?: () => void
  onOverview?: () => void
  onBackToLibrary?: () => void
  onPresenterMode?: () => void
  onShowThumbnails?: () => void
  onShowShortcuts?: () => void
  onShowExport?: () => void
  onExportPDF?: () => void
  onExportPPTX?: () => void
  onPrint?: () => void
  hasPrev?: boolean
  hasNext?: boolean
  showFullscreen?: boolean
  showOverview?: boolean
  showPresenterMode?: boolean
  showThemeToggle?: boolean
  showExport?: boolean
  className?: string
}

export const SlideNavigation: React.FC<SlideNavigationProps> = ({
  onPrev,
  onNext,
  onFullscreen,
  onOverview,
  onBackToLibrary,
  onPresenterMode,
  onShowThumbnails,
  onShowShortcuts,
  onShowExport,
  onExportPDF,
  onExportPPTX,
  onPrint,
  hasPrev = true,
  hasNext = true,
  showFullscreen = true,
  showOverview = false,
  showPresenterMode = false,
  showThemeToggle = true,
  showExport = true,
  className,
}) => {
  return (
    <>
      {/* Bottom Navigation */}
      <div
        className={cn(
          'fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-40',
          className
        )}
      >
        {/* Previous Button */}
        <button
          onClick={onPrev}
          disabled={!hasPrev}
          className={cn(
            'p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg',
            'hover:bg-card hover:scale-110 transition-all duration-200',
            'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100'
          )}
          aria-label="Previous slide"
        >
          <IconSolid name="chevron-left" size="lg" className="text-foreground" />
        </button>

        {/* Thumbnails Button */}
        {onShowThumbnails && (
          <button
            onClick={onShowThumbnails}
            className={cn(
              'p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg',
              'hover:bg-card hover:scale-110 transition-all duration-200'
            )}
            aria-label="Show slide thumbnails"
            title="Thumbnails (T)"
          >
            <IconSolid name="th" className="text-foreground" />
          </button>
        )}

        {/* Overview Button */}
        {showOverview && onOverview && (
          <button
            onClick={onOverview}
            className={cn(
              'p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg',
              'hover:bg-card hover:scale-110 transition-all duration-200'
            )}
            aria-label="Toggle overview"
          >
            <IconSolid name="th" className="text-foreground" />
          </button>
        )}

        {/* Fullscreen Button */}
        {showFullscreen && onFullscreen && (
          <button
            onClick={onFullscreen}
            className={cn(
              'p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg',
              'hover:bg-card hover:scale-110 transition-all duration-200'
            )}
            aria-label="Toggle fullscreen"
            title="Fullscreen (F)"
          >
            <IconSolid name="expand" className="text-foreground" />
          </button>
        )}

        {/* Next Button */}
        <button
          onClick={onNext}
          disabled={!hasNext}
          className={cn(
            'p-3 bg-card/90 backdrop-blur rounded-lg shadow-lg',
            'hover:bg-card hover:scale-110 transition-all duration-200',
            'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100'
          )}
          aria-label="Next slide"
        >
          <IconSolid name="chevron-right" size="lg" className="text-foreground" />
        </button>
      </div>

      {/* Top Right Controls */}
      <div className="fixed top-4 right-4 flex items-center gap-2 z-40">
        {/* Back to Library Button */}
        {onBackToLibrary && (
          <button
            onClick={onBackToLibrary}
            className="px-4 py-2 bg-card/90 backdrop-blur-sm text-foreground rounded-lg shadow-lg hover:bg-card transition-all flex items-center gap-2 group"
            title="Zurück zur Bibliothek (ESC)"
          >
            <IconSolid name="arrow-left" className="group-hover:-translate-x-1 transition-transform" />
            <span className="hidden sm:inline">Zurück</span>
          </button>
        )}

        {/* Presenter Mode Button */}
        {showPresenterMode && onPresenterMode && (
          <button
            onClick={onPresenterMode}
            className="px-3 py-2 bg-card/90 backdrop-blur-sm text-foreground rounded-lg shadow-lg hover:bg-card transition-all flex items-center gap-2"
            title="Presenter mode (P)"
          >
            <IconSolid name="desktop-computer" className="text-muted-foreground" />
            <span className="hidden sm:inline text-sm">Present</span>
          </button>
        )}

        {/* Shortcuts Help Button */}
        {onShowShortcuts && (
          <button
            onClick={onShowShortcuts}
            className="p-2 bg-card/90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-card transition-all"
            aria-label="Show keyboard shortcuts"
            title="Keyboard shortcuts (?)"
          >
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        )}

        {showThemeToggle && <ThemeToggle />}

        {/* Export Button */}
        {showExport && (onShowExport || onExportPDF || onExportPPTX || onPrint) && (
          <button
            onClick={onShowExport || (() => {
              // Show export options - in a real implementation this would open a modal
              if (onPrint) {
                onPrint()
              }
            })}
            className="p-2 bg-card/90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-card transition-all"
            aria-label="Export presentation"
            title="Export (E)"
          >
            <Download className="w-5 h-5 text-muted-foreground" />
          </button>
        )}
      </div>
    </>
  )
}
